import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { JOB_STATUS_VALUES } from "./types/jobTypes";

interface JobMassUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedJobIds: string[];
  onUpdate: () => void;
}

// Convert job status values to options format
const getJobStatusOptions = (t: any) => {
  return JOB_STATUS_VALUES.map(value => ({
    value,
    label: t(`jobs.formWizard.statuses.${value}`, value)
  }));
};

export const JobMassUpdateModal = ({ 
  isOpen, 
  onClose, 
  selectedJobIds, 
  onUpdate 
}: JobMassUpdateModalProps) => {
  const { t } = useTranslation();
  const [fieldValue, setFieldValue] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFieldValue("");
    }
  }, [isOpen]);

  const handleUpdate = async () => {
    if (fieldValue === "" || selectedJobIds.length === 0) {
      toast({
        title: "Error",
        description: "Please select a status",
        variant: "destructive"
      });
      return;
    }

    setIsUpdating(true);

    try {
      // Prepare the update data
      const updateData = { status: fieldValue };

      // Perform batch update
      const { error } = await supabase
        .from('jobs')
        .update(updateData)
        .in('id', selectedJobIds);

      if (error) throw error;

      // When job is closed (won or lost), close all open matches and unpublish
      if (fieldValue === 'closed/won' || fieldValue === 'closed/lost') {
        for (const jobId of selectedJobIds)
          await supabase.rpc('set_match_application_closed', { p_job_id: jobId });
        await supabase
          .from("jobs")
          .update({ published: false, publishdate: null })
          .in("id", selectedJobIds);
        await supabase
          .from("jobs_public")
          .delete()
          .in("job_id", selectedJobIds);
      }

      // Create notifications for status changes
      try {
        // Get job details for notifications
        const { data: jobsData } = await supabase
          .from('jobs')
          .select(`
            id,
            title,
            organization_uuid
          `)
          .in('id', selectedJobIds);

        if (jobsData) {
          // Create notifications for each job
          for (const job of jobsData) {
            try {
              await supabase.rpc('create_org_notifications_with_subscriptions', {
                p_organization_uuid: job.organization_uuid,
                p_type: 'job_status_change',
                p_title: `Bulk Status Update: ${job.title} → ${fieldValue}`,
                p_message: `Job "${job.title}" has been moved to "${fieldValue}" via bulk update.`,
                p_data: {
                  entity_type: 'job',
                  entity_id: job.id,
                  job_title: job.title,
                  new_status: fieldValue,
                  action: 'bulk_status_update'
                },
                p_entity_type: 'job',
                p_entity_id: job.id
              });
            } catch (notificationError) {
              console.warn('Failed to create notification for job:', job.id, notificationError);
            }
          }
        }
      } catch (notificationError) {
        console.warn('Failed to create notifications for bulk update:', notificationError);
      }

      toast({
        title: "Success",
        description: `Successfully updated ${selectedJobIds.length} job(s)`,
      });

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating jobs:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update jobs",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const statusOptions = getJobStatusOptions(t);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mass Update</DialogTitle>
          <DialogDescription>
            Update fields for {selectedJobIds.length} selected job(s)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="field-select">Field to Update</Label>
            <Select value="status" disabled>
              <SelectTrigger id="field-select" className="bg-gray-50">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="status-select">New Value</Label>
            <Select value={fieldValue} onValueChange={setFieldValue}>
              <SelectTrigger id="status-select">
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdate} 
            disabled={fieldValue === "" || isUpdating}
          >
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update {selectedJobIds.length} Job(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
